import { useLingui } from "@lingui/react/macro";
import { offstreetClient } from "../../../../api/client";
import type { RouterOutputs } from "../../../../api/types";
import { useParker } from "../../../../providers/parker";
import { usePermitActiveStep, usePermitFormActions, usePermitSteps } from "../../PermitContextProvider";
import { TermsForm } from "../../components/TermsForm";
type UsePermitTermsStepProps = {
	formId: string;
	permitTypeId: number;
	parkerTermsVersionIds?: number[];
	onSubmit: (data: {
		parkerTermsVersionIds: number[];
	}) => void;
};
export const usePermitTermsStep = ({
	formId,
	permitTypeId,
	parkerTermsVersionIds = [],
	onSubmit,
}: UsePermitTermsStepProps) => {
	const { t } = useLingui();
	const activeStep = usePermitActiveStep();
	const steps = usePermitSteps();
	const { goToStep, setAcceptedTerms } = usePermitFormActions();
	const { data: termsData, isPending: isTermsPending } =
		offstreetClient.parkerTerms.getParkerTermsForPermitType.useQuery({
			permitTypeId,
		});
	const terms = termsData?.items ?? [];
	const { isLoggedIn, parker } = useParker();
	const hasParker = isLoggedIn && !!parker;
	const { data: acceptedVersionsData, isPending: isAcceptedVersionsPending } =
		offstreetClient.parkerTerms.getAcceptedParkerTermsVersions.useQuery(
			{},
			{
				enabled: hasParker,
			},
		);
	const acceptedVersions = acceptedVersionsData?.items ?? [];
	const isAcceptedVersionsLoading = hasParker && isAcceptedVersionsPending;
	const acceptedCurrentVersionIds = terms
		.filter(({ id, parkerTermsVersionId }) =>
			acceptedVersions.some(
				(acceptedVersion) =>
					acceptedVersion.parkerTermsId === id &&
					acceptedVersion.parkerTermsVersionId === parkerTermsVersionId,
			),
		)
		.map(({ parkerTermsVersionId }) => parkerTermsVersionId);
	const { acceptedTerms, currentTerms, isLastTerms, hasAcceptedAllTerms } = splitPermitTermsByAcceptance(
		terms,
		acceptedCurrentVersionIds,
		parkerTermsVersionIds,
	);
	const isPending = isTermsPending || isAcceptedVersionsLoading;
	const primaryActionLabel = !hasAcceptedAllTerms ? t`Accept and Continue` : t`Next`;
	const secondaryAction = !hasAcceptedAllTerms
		? {
				label: t`Decline`,
				onClick: () => {
					if (parkerTermsVersionIds.length === 0) {
						return goToStep(steps[steps.indexOf(activeStep) - 1] ?? steps[0]!);
					}
					setAcceptedTerms({
						parkerTermsVersionIds: parkerTermsVersionIds.slice(0, -1),
					});
				},
			}
		: undefined;
	return {
		title: t`Parker Terms`,
		component: (
			<TermsForm
				key={currentTerms?.parkerTermsVersionId}
				formId={formId}
				currentTerms={currentTerms}
				isPending={isPending}
				acceptedTerms={acceptedTerms}
				onAccept={isLastTerms ? onSubmit : setAcceptedTerms}
			/>
		),
		isLastTerms,
		hasAcceptedAllTerms,
		primaryActionLabel,
		secondaryAction,
	};
};
export const splitPermitTermsByAcceptance = (
	terms: RouterOutputs["parkerTerms"]["getParkerTermsForPermitType"]["items"],
	acceptedVersionIds: number[],
	currentAcceptedVersionIds: number[] = [],
) => {
	const acceptedIds = new Set([...acceptedVersionIds, ...currentAcceptedVersionIds]);
	const acceptedTerms = terms.filter(({ parkerTermsVersionId }) => acceptedIds.has(parkerTermsVersionId));
	const remainingTerms = terms.filter(({ parkerTermsVersionId }) => !acceptedIds.has(parkerTermsVersionId));
	return {
		acceptedTerms,
		currentTerms: remainingTerms[0],
		isLastTerms: remainingTerms.length <= 1,
		hasAcceptedAllTerms: terms.length > 0 && remainingTerms.length === 0,
	};
};
